import { useMemo } from "react";

import type { PublishedNavigationElement } from "@/lib/api/types";
import {
  LAND_WARNINGS_SLUG,
  landWarningsPath,
  MARITIME_WARNINGS_SLUG,
  maritimeWarningsPath,
} from "@/features/dashboard/features/general/warning-page-paths";
import { useDashboardWarningSettings } from "@/features/shared/hooks";
import { usePublicWarningPageDefaults } from "@/features/shared/hooks/use-warning-page-defaults";
import { hasMaritimeWarningsEnabled } from "@/lib/maritime-warning-page-settings.utils";
import {
  type KindedNavigationElement,
  NAVIGATION_ELEMENT_KIND,
  withBuiltInWarningPages,
} from "@/lib/navigation-element-kinds";
import { readPublicWarningPageSettings } from "@/lib/public-warning-settings.utils";

/**
 * Interim: the backend does not create warning page navigation elements yet, so the
 * dashboard nav adds them for enabled pages. Dashboard-only; the admin reads its own
 * navigation endpoint and never sees them. Empty `title` falls back to the locale label.
 */
export function useNavigationElementsWithWarningPages(
  navigationElements: PublishedNavigationElement[] | undefined,
): KindedNavigationElement<PublishedNavigationElement>[] | undefined {
  const { selectedDashboard, publicWarningSettings, maritimeWarningPage } =
    useDashboardWarningSettings();
  const publicDefaults = usePublicWarningPageDefaults();
  const landEnabled = readPublicWarningPageSettings(
    publicWarningSettings,
    publicDefaults,
  ).enabled;
  const maritimeEnabled = hasMaritimeWarningsEnabled(maritimeWarningPage);
  const slug = selectedDashboard?.slug;

  return useMemo(() => {
    if (!navigationElements || !slug) return navigationElements;
    const builtIns: KindedNavigationElement<PublishedNavigationElement>[] = [];
    if (landEnabled) {
      builtIns.push({
        id: `built-in-${NAVIGATION_ELEMENT_KIND.publicWarningsPage}`,
        slug: LAND_WARNINGS_SLUG,
        title: "",
        kind: NAVIGATION_ELEMENT_KIND.publicWarningsPage,
        full_path: landWarningsPath(slug),
      });
    }
    if (maritimeEnabled) {
      builtIns.push({
        id: `built-in-${NAVIGATION_ELEMENT_KIND.maritimeWarningsPage}`,
        slug: MARITIME_WARNINGS_SLUG,
        title: "",
        kind: NAVIGATION_ELEMENT_KIND.maritimeWarningsPage,
        full_path: maritimeWarningsPath(slug),
      });
    }
    return withBuiltInWarningPages<KindedNavigationElement<PublishedNavigationElement>>(
      navigationElements,
      builtIns,
    );
  }, [navigationElements, slug, landEnabled, maritimeEnabled]);
}
